import { useState, useCallback } from 'react';
import {
  Download,
  Upload,
  Database,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  FileText,
  HardDrive,
  Shield,
  Check,
} from 'lucide-react';
import { clsx } from 'clsx';
import api from '../config/api';
import syncService from '../services/syncService';

export default function Backup() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [popup, setPopup] = useState(null);

  const showPopup = useCallback((message, type = 'success') => {
    setPopup({ message, type });
    setTimeout(() => setPopup(null), 5000);
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      await api.exportBackup();
      showPopup('Database backup exported successfully!');
    } catch (err) {
      showPopup(err.message || 'Failed to export backup', 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.toLowerCase().endsWith('.zip')) {
        showPopup('Please select a valid .zip backup file', 'error');
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      showPopup('Please select a backup file first', 'error');
      return;
    }

    setShowConfirmModal(false);
    setImporting(true);
    setImportResult(null);

    try {
      const data = await api.importBackup(selectedFile);
      setImportResult(data);
      showPopup(`Backup restored successfully: ${data.summary?.restored_records ?? data.summary?.inserted_records ?? 0} records restored.`);

      setSelectedFile(null);
      const fileInput = document.getElementById('backup-file-input');
      if (fileInput) fileInput.value = '';

      // Reset and pull fresh data to IndexedDB mirror
      await syncService.resetAndPullFromServer();
    } catch (err) {
      setImportResult({ error: err.message });
      showPopup(err.message || 'Failed to restore backup', 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {popup && (
        <div
          className={clsx(
            'fixed top-5 right-5 z-50 flex items-center gap-3 rounded-xl border px-5 py-3.5 shadow-lg transition-all',
            popup.type === 'success'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'border-red-200 bg-red-50 text-red-700'
          )}
        >
          {popup.type === 'success' ? (
            <CheckCircle className="h-5 w-5 text-emerald-600" />
          ) : (
            <XCircle className="h-5 w-5 text-red-600" />
          )}
          <p className="text-sm font-semibold">{popup.message}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Database Backup & Restore</h1>
          <p className="text-sm text-gray-500 mt-1">Export your complete database archive or restore system state to a backup snapshot</p>
        </div>
      </div>

      {/* Export Section */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100">
              <Download className="h-4 w-4 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Export Database Backup</h2>
              <p className="text-xs text-gray-500">Download a complete backup of all pharmacy tables</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4">
            <div className="flex items-center gap-3 mb-3">
              <Database className="h-5 w-5 text-emerald-600" />
              <p className="text-sm font-medium text-gray-700">What's included in the backup:</p>
            </div>
            <ul className="text-sm text-gray-600 space-y-1.5 pl-8">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                All medicines, categories, suppliers, and customers
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                Sales, purchases, and inventory batches
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                Staff, users, and system settings
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                Expenses, refunds, payments, and accounting records
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                Expiry alerts, damage logs, and stock levels
              </li>
            </ul>
          </div>

          <button
            onClick={handleExport}
            disabled={exporting}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Creating Backup...
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Export Database Backup
              </>
            )}
          </button>

          <p className="text-xs text-gray-400 text-center">
            The backup is downloaded as a ZIP file containing JSON files for each table + a manifest.json
          </p>
        </div>
      </div>

      {/* Import Section */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100">
              <Upload className="h-4 w-4 text-amber-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Import & Restore Database Backup</h2>
              <p className="text-xs text-gray-500">Restore pharmacy data from a previously exported backup file</p>
            </div>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-lg border border-amber-100 bg-amber-50 p-4">
            <div className="flex items-center gap-3 mb-3">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              <p className="text-sm font-medium text-gray-700">Important Notes:</p>
            </div>
            <ul className="text-sm text-gray-600 space-y-1.5 pl-8">
              <li className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <strong>Full State Rollback:</strong> Restoring this backup will replace current records with the snapshot data.
              </li>
              <li className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-amber-500 flex-shrink-0" />
                Transactions, sales, and payments recorded after the backup date will be rolled back.
              </li>
              <li className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-amber-500 flex-shrink-0" />
                The backup must be a valid .ZIP file exported from this system.
              </li>
              <li className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-amber-500 flex-shrink-0" />
                Database integrity is protected by automatic transaction rollback on errors.
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-gray-500">Select Backup File (.zip)</span>
              <div className="relative">
                <input
                  id="backup-file-input"
                  type="file"
                  accept=".zip"
                  onChange={handleFileSelect}
                  disabled={importing}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div
                  className={clsx(
                    'flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors',
                    selectedFile
                      ? 'border-emerald-300 bg-emerald-50'
                      : 'border-gray-200 bg-gray-50 hover:border-emerald-300 hover:bg-emerald-50'
                  )}
                >
                  {selectedFile ? (
                    <div className="flex items-center gap-3 w-full max-w-md">
                      <FileText className="h-10 w-10 text-emerald-500 flex-shrink-0" />
                      <div className="flex-1 text-left min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                        <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                          const fileInput = document.getElementById('backup-file-input');
                          if (fileInput) fileInput.value = '';
                        }}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <XCircle className="h-5 w-5" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <HardDrive className="h-10 w-10 text-gray-300" />
                      <p className="text-sm text-gray-500">Click to select or drag and drop a .zip backup file</p>
                    </>
                  )}
                </div>
              </div>
            </label>

            <button
              type="button"
              onClick={() => setShowConfirmModal(true)}
              disabled={importing || !selectedFile}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Restoring Backup...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Restore Database Backup
                </>
              )}
            </button>
          </div>

          {importResult && !importResult.error && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
                <CheckCircle className="h-5 w-5" />
                Database Restoration Completed Successfully
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{importResult.summary?.total_tables || 0}</p>
                  <p className="text-xs text-gray-500">Tables Restored</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{importResult.summary?.total_records || 0}</p>
                  <p className="text-xs text-gray-500">Total in Backup</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{importResult.summary?.restored_records ?? importResult.summary?.inserted_records ?? 0}</p>
                  <p className="text-xs text-gray-500">Restored Records</p>
                </div>
              </div>
              <details className="text-xs text-gray-600">
                <summary className="cursor-pointer font-medium text-gray-700 mb-2">View Table Details</summary>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {importResult.details?.map((detail, idx) => (
                    <div key={idx} className="flex justify-between text-xs py-0.5 border-b border-gray-100 last:border-0">
                      <span className="font-mono text-gray-800">{detail.table}</span>
                      <span className="text-emerald-700 font-medium">{detail.restored ?? detail.inserted} / {detail.total} records restored</span>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}

          {importResult?.error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-red-700">
                <XCircle className="h-5 w-5" />
                Restore Failed
              </div>
              <p className="text-sm text-red-600 mt-1">{importResult.error}</p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedFile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl space-y-4 animate-in fade-in zoom-in duration-150">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">Confirm Database Restoration</h3>
                <p className="text-xs text-gray-500">Roll back database to backup snapshot</p>
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 bg-gray-50 p-3.5 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">Backup File:</span>
                <span className="font-semibold text-gray-900 truncate max-w-[200px]">{selectedFile.name}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">File Size:</span>
                <span className="font-semibold text-gray-900">{(selectedFile.size / 1024).toFixed(1)} KB</span>
              </div>
            </div>

            <p className="text-xs text-gray-600 leading-relaxed">
              <strong className="text-rose-600">Warning:</strong> Restoring this backup will replace current database tables with the data from this archive. Any sales, payments, purchases, or changes made <strong>after</strong> this backup was created will be rolled back.
            </p>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleImport}
                className="rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white hover:bg-rose-700 shadow-sm transition-colors"
              >
                Yes, Restore Database
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}